import { Lang, DEFAULT_LANG } from '../i18n/locales';
import { brandContent } from '../content/brand';

/**
 * Normalize locale string to ensure it matches our internal keys.
 * Handles 'zh-HK' -> 'zh-hk', 'zh-TW' -> 'zh-hk', etc.
 */
export function normalizeLocale(lang: string | undefined | null): Lang {
  if (!lang) return DEFAULT_LANG;
  const lower = lang.toLowerCase();
  if (lower === 'zh-hk' || lower === 'zh-tw' || lower === 'zh-hant') return 'zh-hk';
  // Note: zh-cn fallback to DEFAULT_LANG (en) since it's removed
  return DEFAULT_LANG;
}

/**
 * Maps a UI locale (en, zh-hk) to the Database locale schema (en, zh).
 */
export function mapLocaleToDb(lang: Lang): 'en' | 'zh' {
  if (lang === 'en') return 'en';
  if (lang === 'zh-hk') return 'zh';
  return 'en'; // Fallback
}

/**
 * Defensive Helper: Ensures value is always an Array
 */
export const safeArray = <T>(v: unknown): T[] => (Array.isArray(v) ? v : []);

/**
 * Defensive Helper: Ensures value is always an Object
 */
export const safeItem = <T = Record<string, unknown>>(v: unknown, fallback: T = {} as T): T =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as T) : fallback;

/**
 * Safe Dictionary Access: t('nav.about')
 * Supports dot-notation paths and automatically fallbacks to English.
 */
export function t(path: string, lang: Lang): string {
  const safeLang = normalizeLocale(lang);
  const keys = path.split('.');

  // 1. Try Target Language
  let current: unknown = brandContent[safeLang] || brandContent[DEFAULT_LANG];
  for (const key of keys) {
    if (current === undefined || current === null) break;
    current = (current as Record<string, unknown>)[key];
  }

  if (typeof current === 'string') return current;

  // 2. Fallback to English (if we weren't already there)
  if (safeLang !== DEFAULT_LANG) {
    current = brandContent[DEFAULT_LANG];
    for (const key of keys) {
      if (current === undefined || current === null) break;
      current = (current as Record<string, unknown>)[key];
    }
  }

  // 3. Last resort: Return the path itself or capitalized last segment
  return typeof current === 'string' ? current : keys[keys.length - 1] || path;
}

/**
 * Core Helper: Safe Localized Text Retrieval
 */
export function getLocalizedField<T>(
  field: unknown,
  lang: Lang,
  fallbackLang: Lang = DEFAULT_LANG
): T {
  if (!field) return '' as unknown as T;

  const f = safeItem<Record<string, unknown>>(field);
  const safeLang = normalizeLocale(lang);

  // 1. Try Direct Match
  let val = f[safeLang];

  // 2. Fuzzy Match for Chinese
  if (isEmpty(val) && safeLang === 'zh-hk') {
    val = f['zh'] || f['zh-hk'];
  }

  // 3. Fallback to English
  if (isEmpty(val)) {
    val = f[fallbackLang] || f['en'];
  }

  // 4. Ultimate Fallback
  if (isEmpty(val)) {
    if (Array.isArray(f['en'])) return [] as unknown as T;
    return '' as unknown as T;
  }

  return val as T;
}

export const getLocalizedText = (
  entity: Record<string, unknown> | null | undefined,
  field: string,
  lang: Lang
): string => {
  if (!entity) return '';
  return getLocalizedField<string>(entity[field], lang);
};

export function hasTranslation(
  field: Record<string, unknown> | null | undefined,
  lang: Lang
): boolean {
  if (!field) return false;
  const safeLang = normalizeLocale(lang);
  const hasExact = !isEmpty(field[safeLang]);
  if (hasExact) return true;

  if (safeLang === 'zh-hk') {
    return !isEmpty(field['zh']) || !isEmpty(field['zh-hk']);
  }

  return false;
}

function isEmpty(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string' && v.trim() === '') return true;
  if (Array.isArray(v) && v.length === 0) return true;
  return false;
}

export function generateSlug(text: string): string {
  if (!text) return '';
  return text
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w\u4e00-\u9fa5-]/g, '')
    .replace(/^-+|-+$/g, '');
}
